import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { usePlatformConfig } from "@kivunova/kivufrontendcommon";
import type { RootState } from "../store/store";
import {
  getClarityRouteArea,
  identifyClarityUser,
  isClarityReady,
  setClarityTags
} from "./clarity";
import { getOrCreateClaritySessionId } from "./claritySessionCookie";

/**
 * SPA journey tracker: re-identify on route changes and tag sessions from profile.
 * Must render inside BrowserRouter (and ideally under PlatformConfigProvider).
 */
const ClarityTracker: React.FC = () => {
  const location = useLocation();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);
  const { config } = usePlatformConfig();
  const lastPageIdRef = useRef<string | null>(null);
  const lastCustomIdRef = useRef<string | null>(null);
  const cSessionIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isClarityReady()) return;

    const cSessionId = cSessionIdRef.current ?? getOrCreateClaritySessionId();
    cSessionIdRef.current = cSessionId;

    const pageId = location.pathname;
    const customId = userDetails?.id ? String(userDetails.id) : cSessionId;

    const pageChanged = lastPageIdRef.current !== pageId;
    const userChanged = lastCustomIdRef.current !== customId;
    if (!pageChanged && !userChanged) return;

    lastPageIdRef.current = pageId;
    lastCustomIdRef.current = customId;

    identifyClarityUser({
      userId: customId,
      sessionId: cSessionId,
      pageId
    });
    setClarityTags({
      route_area: getClarityRouteArea(pageId),
      c_sessionId: cSessionId,
      environment:
        process.env.REACT_APP_ENVIRONMENT?.trim() || process.env.NODE_ENV
    });
  }, [location.pathname, userDetails?.id]);

  useEffect(() => {
    if (!isClarityReady()) return;
    if (!userDetails?.id) return;

    const cSessionId = cSessionIdRef.current ?? getOrCreateClaritySessionId();
    cSessionIdRef.current = cSessionId;

    setClarityTags({
      c_sessionId: cSessionId,
      companyId: userDetails.companyId
        ? String(userDetails.companyId)
        : undefined,
      storeId: userDetails.storeId ? String(userDetails.storeId) : undefined,
      role: userDetails.role ? String(userDetails.role) : undefined,
      environment:
        process.env.REACT_APP_ENVIRONMENT?.trim() || process.env.NODE_ENV,
      businessType: config?.businessType
        ? String(config.businessType)
        : undefined
    });
  }, [
    userDetails?.id,
    userDetails?.companyId,
    userDetails?.storeId,
    userDetails?.role,
    config?.businessType
  ]);

  return null;
};

export default ClarityTracker;
